<template>
  <div class="sale-filter">
    <v-menu
      ref="menu"
      v-model="open"
      offset-y
      :left="menuLeft"
      nudge-bottom="4"
      min-width="0"
      max-width="none"
      eager
      :close-on-content-click="false"
      content-class="app-menu app-menu--picker"
      role="none"
    >
      <template v-slot:activator="{ on, attrs }">
        <app-button
          kind="field"
          icon="funnel"
          chevron
          :title="description || null"
          aria-haspopup="dialog"
          v-bind="attrs"
          v-on="on"
        >Filter<span v-if="count" class="app-sr-only">, {{ count }} aktiv</span><span
          class="sale-filter__badge"
          :class="{ 'sale-filter__badge--touch': touch, 'sale-filter__badge--leer': !count }"
          aria-hidden="true"
        >{{ count || 1 }}</span></app-button>
      </template>
      <div
        ref="panel"
        class="sale-filter__panel"
        :class="panelClasses"
        :style="panelStyle"
        role="dialog"
        aria-label="Filter"
        tabindex="-1"
        @keydown.tab="trapTab"
        @keydown.esc="onEsc"
      >
        <div class="sale-filter__cols">
          <div v-scroll-hint class="sale-filter__body" :style="mainStyle">
            <div v-for="segment in segments" :key="segment.key" class="sale-filter__block">
              <span class="app-label sale-filter__label" aria-hidden="true">{{ segment.label }}</span>
              <app-segment
                :value="value[segment.key] || null"
                :items="segment.items"
                :label="segment.label"
                @input="patch(segment.key, $event)"
              />
            </div>

            <div v-if="facets.length || timeDefs.length" class="sale-filter__lists">
              <div v-for="list in facets" :key="list.key" class="sale-filter__list">
                <button
                  type="button"
                  class="sale-filter__toggle"
                  :class="{ 'sale-filter__toggle--current': sideOpen && shownKey === list.key }"
                  :data-list="list.key"
                  data-flush
                  :aria-expanded="shownKey === list.key ? 'true' : 'false'"
                  :aria-controls="shownKey === list.key ? (wide ? sideId : listId(list.key)) : null"
                  @click="toggleList(list.key, $event)"
                  @keydown="onToggleKey(list.key, $event)"
                >
                  <span class="sale-filter__name">{{ list.label }}<span class="app-sr-only">:</span></span>
                  <span
                    class="sale-filter__summary"
                    :class="{ 'sale-filter__summary--set': selectedKeys(list.key).length > 0 }"
                  >{{ summaryText(list) }}</span>
                  <app-icon
                    :name="!wide && shownKey === list.key ? 'chevD' : 'chevR'"
                    :size="14"
                    class="sale-filter__chev"
                  />
                </button>
                <!-- schmale Fenster: Auswahl unter dem Eintrag (gleicher Inhalt wie die zweite Spalte) -->
                <div
                  v-if="!wide && shownKey === list.key"
                  :id="listId(list.key)"
                  class="sale-filter__options"
                  role="group"
                  :aria-label="list.label"
                >
                  <div v-if="searchable(list)" class="sale-filter__find">
                    <app-search v-model="listQuery" placeholder="In Liste suchen" :debounce="0" />
                  </div>
                  <app-checkbox
                    class="sale-filter__option sale-filter__option--alle"
                    :value="!selectedKeys(list.key).length"
                    @input="clearList(list.key)"
                  >Alle</app-checkbox>
                  <app-checkbox
                    v-for="item in shownItems(list)"
                    :key="item.key"
                    class="sale-filter__option"
                    :value="isChecked(list.key, item.key)"
                    @input="check(list.key, item.key, $event)"
                    @mouseenter.native="titleIfClipped"
                  ><app-icon v-if="item.icon" :name="item.icon" :size="14" class="sale-filter__icon" />{{ item.text }}<span v-if="item.missing" class="sale-filter__missing"> · nicht im Zeitraum</span></app-checkbox>
                  <div v-if="!shownItems(list).length" class="sale-filter__none" aria-hidden="true">Keine Treffer</div>
                  <span class="app-sr-only" role="status">{{ matchText(list) }}</span>
                </div>
              </div>
              <!-- Zeitpunkte (Kundenwunsch 14.10.2026): Eintrag wie eine Liste, die Zeitauswahl steht rechts bzw. darunter -->
              <div v-for="time in timeDefs" :key="'zeit-' + time.key" class="sale-filter__list">
                <button
                  type="button"
                  class="sale-filter__toggle"
                  :class="{ 'sale-filter__toggle--current': sideOpen && shownKey === timeKey(time) }"
                  :data-list="timeKey(time)"
                  data-flush
                  :aria-expanded="shownKey === timeKey(time) ? 'true' : 'false'"
                  :aria-controls="shownKey === timeKey(time) ? (wide ? sideId : listId(timeKey(time))) : null"
                  @click="toggleList(timeKey(time), $event)"
                  @keydown="onToggleKey(timeKey(time), $event)"
                >
                  <span class="sale-filter__name">{{ time.label }}<span class="app-sr-only">:</span></span>
                  <span
                    class="sale-filter__summary"
                    :class="{ 'sale-filter__summary--set': !!timeSummary(time) }"
                  >{{ timeSummary(time) || 'alle' }}</span>
                  <app-icon
                    :name="!wide && shownKey === timeKey(time) ? 'chevD' : 'chevR'"
                    :size="14"
                    class="sale-filter__chev"
                  />
                </button>
                <div
                  v-if="!wide && shownKey === timeKey(time)"
                  :id="listId(timeKey(time))"
                  class="sale-filter__options sale-filter__options--time"
                  role="group"
                  :aria-label="time.label"
                >
                  <filter-time-range
                    ref="timeEditor"
                    :label="time.label"
                    :from="timeValueOf(time, 'from')"
                    :to="timeValueOf(time, 'to')"
                    :days="timeDays[time.key] || []"
                    @change="onTimeChange(time, $event)"
                  />
                </div>
              </div>
            </div>

            <div v-if="showRange" class="sale-filter__block">
              <span :id="priceId" class="app-label sale-filter__label">{{ rangeLabel }}</span>
              <div class="sale-filter__price" role="group" :aria-labelledby="priceId">
                <app-text-field
                  :value="draftMin"
                  inputmode="decimal"
                  autocomplete="off"
                  placeholder="von"
                  :aria-label="rangeLabel + ' von (€)'"
                  :aria-describedby="minInvalid ? priceErrorId : null"
                  :error="minInvalid"
                  @input="onPrice('min', $event)"
                  @blur="flushDrafts"
                  @keydown.enter="flushDrafts"
                />
                <span class="sale-filter__dash" aria-hidden="true">–</span>
                <app-text-field
                  :value="draftMax"
                  inputmode="decimal"
                  autocomplete="off"
                  placeholder="bis"
                  :aria-label="rangeLabel + ' bis (€)'"
                  :aria-describedby="maxInvalid ? priceErrorId : null"
                  :error="maxInvalid"
                  @input="onPrice('max', $event)"
                  @blur="flushDrafts"
                  @keydown.enter="flushDrafts"
                />
                <span class="sale-filter__unit" aria-hidden="true">€</span>
              </div>
              <p v-if="minInvalid || maxInvalid" :id="priceErrorId" class="app-field__error sale-filter__error">
                <app-icon name="alert" :size="14" />{{ rangeLabel }} als Zahl eingeben, z. B. 2,50
              </p>
            </div>
          </div>

          <!-- breite Fenster: Auswahl einer Liste als zweite Spalte rechts, von Anfang an (Kundenwunsch 12.10.2026);
               die Spalte ist so hoch wie die linke, lange Listen scrollen in ihr -->
          <div
            v-if="sideOpen"
            :id="sideId"
            ref="side"
            class="sale-filter__side"
            :style="sideStyle"
            role="group"
            :aria-labelledby="sideId + '-titel'"
            @keydown="onSideKey"
          >
            <div v-if="currentTime" class="sale-filter__side-inner">
              <span :id="sideId + '-titel'" class="app-label sale-filter__side-head">{{ currentTime.label }}</span>
              <div v-scroll-hint class="sale-filter__side-body">
                <filter-time-range
                  :key="currentTime.key"
                  ref="timeEditor"
                  :label="currentTime.label"
                  :from="timeValueOf(currentTime, 'from')"
                  :to="timeValueOf(currentTime, 'to')"
                  :days="timeDays[currentTime.key] || []"
                  @change="onTimeChange(currentTime, $event)"
                />
              </div>
            </div>
            <div v-else class="sale-filter__side-inner">
              <span :id="sideId + '-titel'" class="app-label sale-filter__side-head">{{ currentList.label }}</span>
              <!-- Suchfeld bleibt stehen, die Werte darunter scrollen -->
              <div v-if="searchable(currentList)" class="sale-filter__find">
                <app-search v-model="listQuery" placeholder="In Liste suchen" :debounce="0" />
              </div>
              <div v-scroll-hint class="sale-filter__side-body">
                <app-checkbox
                  class="sale-filter__option sale-filter__option--alle"
                  :value="!selectedKeys(currentList.key).length"
                  @input="clearList(currentList.key)"
                >Alle</app-checkbox>
                <app-checkbox
                  v-for="item in shownItems(currentList)"
                  :key="item.key"
                  class="sale-filter__option"
                  :value="isChecked(currentList.key, item.key)"
                  @input="check(currentList.key, item.key, $event)"
                  @mouseenter.native="titleIfClipped"
                ><app-icon v-if="item.icon" :name="item.icon" :size="14" class="sale-filter__icon" />{{ item.text }}<span v-if="item.missing" class="sale-filter__missing"> · nicht im Zeitraum</span></app-checkbox>
                <div v-if="!shownItems(currentList).length" class="sale-filter__none" aria-hidden="true">Keine Treffer</div>
                <span class="app-sr-only" role="status">{{ matchText(currentList) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="sale-filter__foot">
          <span class="sale-filter__result" role="status">{{ resultText }}</span>
          <!-- „Übernehmen“ schließt das Panel (Kundenwunsch 14.10.2026: nicht nur herausklicken; die Auswahl wirkt wie
               bisher sofort, ein eingetippter Preis wird dabei übernommen) -->
          <div class="sale-filter__foot-actions">
            <app-button :disabled="!count && !draftMin && !draftMax" @click="reset">Zurücksetzen</app-button>
            <app-button @click="apply">Übernehmen</app-button>
          </div>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<script>
// Filter-Panel der Verkäufe (Kundenwunsch 12.10.2026: Filter der früheren Filterzeile im neuen Design, ohne
// Backend-Änderung; Logik in saleFilters.js). Feldknopf mit Trichter und „Filter“, bei aktiven Filtern dahinter die
// Anzahl als blaue Pille wie die angehakten Checkboxen (Kundenwunsch 12.10.2026; zugänglicher Name „Filter, 2 aktiv“,
// gesetzte Filter als Tooltip über description). Ohne Filter hält eine unsichtbare Pille den Platz, die Leiste rückt
// beim Setzen des ersten Filters nicht. Menü wie PeriodField: Segmente Zahlart, Storno und Richtung,
// Mehrfachlisten aus den geladenen Zeilen (facets), Preis von–bis, unten Ergebnis „n Verkäufe“ und „Zurücksetzen“
// (nur die Filter dieses Panels). Jede Änderung wirkt sofort (input mit dem neuen Zustand), der Preis entprellt
// (300 ms, Enter und Verlassen des Felds sofort).
// Listen: ab 960 px Fensterbreite (Desktop, Tablet quer) hat das Panel von Anfang an eine zweite Spalte rechts mit der
// Auswahl einer Liste (Kundenwunsch 12.10.2026): beim Öffnen die erste Liste mit Auswahl, sonst die erste; ein anderer
// Eintrag wechselt den Inhalt, die Breite bleibt. Schmaler (Tablet hoch, Handy) klappt ein Eintrag seine Auswahl wie
// bisher darunter auf und wieder zu. Die Auswahl hat „Alle“ (keine Einschränkung) und ab 12 Werten ein Suchfeld.
// Zeitpunkte (timeDefs, seit 14.10.2026, z. B. „Letzte Änderung“ der Shop-Tickets): Einträge unter den Listen wie diese
// („alle“ bzw. „13.10., 00:00–05:24“); die Zeitauswahl (FilterTimeRange.vue: von und bis mit Datum und Uhrzeit, Kalender
// bzw. Uhrzeitliste am angeklickten Feld) steht in der zweiten Spalte bzw. unter dem Eintrag.
// Keine nativen Datumsfelder: die zeigen bei englisch eingestelltem Browser „mm/dd/yyyy“ und AM/PM. Noch nicht
// gemeldete Eingaben der Zeitauswahl übernimmt das Panel zusammen mit dem Betrag in einem Ereignis.
// Ereignisse: input (Filterzustand), open (die Seite berechnet dann facets neu), close.
// Tastatur: beim Öffnen Fokus auf den ersten Knopf, Tab bleibt im Panel, Esc schließt und gibt den Fokus an den
// Feldknopf zurück (VMenu); nach einem Klick auf eine freie Stelle hält das Panel selbst den Fokus (tabindex −1), Esc
// und Tab wirken weiter. Zweite Spalte: Enter, Leertaste und Pfeil rechts am Eintrag zeigen seine Liste und setzen
// den Fokus auf ihr erstes Element; Pfeil links und Esc führen zum Eintrag zurück, erst das nächste Esc schließt das
// Panel. Das Panel enthält keine Elemente mit der Klasse v-list-item, sonst übernimmt VMenu Pfeil- und Tab-Tasten und
// schließt bei Tab. Screenreader: Einträge heißen „Fahrer: alle“, eine Statuszeile sagt die Treffer der Listensuche
// an, ein unlesbarer Preis hat eine Meldung (aria-describedby).
// Lage (place, seit der Prüfung vom 12.10.2026 nach dem Platz am Knopf statt nur nach der Fensterbreite): mit alignRight
// rechtsbündig zum Knopf (Verkäufe: Knopf rechts in der Werkzeugleiste), sonst linksbündig. Zweispaltig nur, wenn das
// breite Panel auf dieser Seite mit 12 px Abstand zum Fensterrand passt; sonst einspaltig auf der bevorzugten Seite,
// passt auch das nicht, auf der anderen (Tablet hoch: linksbündig am Knopf). Weil Breite und Seite beim Öffnen
// feststehen, springt nichts, wenn man einen anderen Eintrag wählt.
// Maße: Maus 360 breit, Touch 400, Handy Fensterbreite − 24 (samt Kante); mit zweiter Spalte Maus 320 + 280, Touch
// 360 + 320. Höchstens bis 12 px über den unteren Fensterrand (Handy höchstens 70 % der Fensterhöhe), Inhalt und
// Spalte scrollen mit Scrollschatten, Ergebnis und „Zurücksetzen“ bleiben stehen. Die zweite Spalte ist so hoch wie
// die linke: Ein Wechsel der Liste ändert die Höhe des Panels nicht. Lange Werte brechen in bis zu zwei Zeilen um,
// darüber hinaus gekürzte zeigen den vollen Wert als Tooltip.
// min-width="0" statt "auto": mit "auto" rechnet VMenu die Menübreite als NaN und schiebt ein zu breites Menü nicht vom
// rechten Rand zurück (Handy). max-width="none": Vuetify begrenzt Menüs sonst auf 80 % der Breite und schneidet das
// Panel ab; die Grenze trägt das Panel selbst. eager: der Inhalt ist schon vor dem ersten Öffnen gemessen.
import { raiseMenu } from '@/components/ui/AppSelect.vue';
import { formatNumber, plural } from '@/utils/format';
import panelMenu, { fitsAt, alignRightFor } from './panelMenu';
import FilterTimeRange from './FilterTimeRange.vue';
import {
  PAYMENT_ITEMS,
  STORNO_ITEMS,
  DIRECTION_ITEMS,
  emptyFilters,
  activeCount,
  priceInvalid,
  timeFields,
  shortTimeRange,
  foldText
} from './saleFilters';

const SEGMENTS = [
  { key: 'payment', label: 'Zahlart', items: PAYMENT_ITEMS },
  { key: 'storno', label: 'Storno', items: STORNO_ITEMS },
  { key: 'direction', label: 'Richtung', items: DIRECTION_ITEMS }
];
const PRICE_DELAY = 300;
// zweite Spalte ab dieser Fensterbreite, unabhängig von der Dichte (und nur, wenn das breite Panel am Knopf passt)
const WIDE_MIN = 960;
// Breite einspaltig (ohne Kante; Handy: Fensterbreite − 26)
const SINGLE = { maus: 360, touch: 400 };
// Breiten mit zweiter Spalte: Hauptspalte (Segmente passen bei Touch knapp in 360) und Spalte der Liste
const COLUMNS = { maus: { main: 320, side: 280 }, touch: { main: 360, side: 320 } };
// Suchfeld in einer Liste ab so vielen Werten
const SEARCH_MIN = 12;
// Mindesthöhe des Panels (Kundenwunsch 13.10.2026): wenige Filter (Zahlungen, Saldo, Fahrerabrechnung) ergäben sonst
// ein flaches Panel, die Fußzeile rückt dann ganz nach unten; Verkäufe ist ohnehin höher
const MIN_HEIGHT = { maus: 440, touch: 480, handy: 360 };
// mit Zeitpunkten: Platz für den aufgeklappten Kalender unter der Zeile „bis“ in der zweiten Spalte
const MIN_HEIGHT_TIMES = { maus: 500, touch: 600 };

// Texteingaben behalten Pfeil links für die Schreibmarke
function isTextInput(el) {
  return !!el && el.tagName === 'INPUT' && el.type !== 'checkbox' && el.type !== 'radio';
}

export default {
  name: 'SaleFilterMenu',
  components: { FilterTimeRange },
  // Höhe, Fokus beim Öffnen und Tab im Panel (panelMenu.js)
  mixins: [panelMenu],
  props: {
    // Filterzustand (emptyFilters aus saleFilters.js)
    value: { type: Object, required: true },
    // Listen mit Werten (buildFacets aus saleFilters.js)
    facets: { type: Array, default: () => [] },
    // Anzahl Verkäufe nach Filter und Suche
    resultCount: { type: Number, default: null },
    // gesetzte Filter als Text (Tooltip und Screenreader)
    description: { type: String, default: null },
    // Panel rechtsbündig zum Knopf (die Seite setzt es, solange die Bedienelemente rechts in der Leiste stehen)
    alignRight: { type: Boolean, default: false },
    // Wiederverwendung auf anderen Seiten (Zahlungen, Kundenwunsch 13.10.2026); ohne Angabe gilt Verkäufe:
    // Umschalter oben (null = Zahlart, Storno, Richtung), Anzahl aktiver Filter (null = activeCount), Bezeichnung des
    // Betrags, Wörter für das Ergebnis und der leere Zustand für „Zurücksetzen“
    segmentDefs: { type: Array, default: null },
    activeFilters: { type: Number, default: null },
    rangeLabel: { type: String, default: 'Preis' },
    resultNouns: { type: Array, default: () => ['Verkauf', 'Verkäufe'] },
    emptyValue: { type: Function, default: null },
    // Bereich von–bis anzeigen (Fahrerabrechnung ohne Betrag)
    showRange: { type: Boolean, default: true },
    // Zeitpunkte von–bis: [{ key, label }] (config.times aus saleFilters.js) und Tage mit Einträgen je Zeitpunkt
    // (buildTimeDays)
    timeDefs: { type: Array, default: () => [] },
    timeDays: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      // gewählte Liste: schmal die aufgeklappte, breit die in der zweiten Spalte (null: noch keine gewählt)
      openList: null,
      // Suchtext der offenen Liste (gilt nur für sie, beim Wechsel leer)
      listQuery: '',
      draftMin: '',
      draftMax: '',
      // passt das zweispaltige Panel am Knopf (place beim Öffnen)?
      wideFits: true
    };
  },
  computed: {
    count() {
      return this.activeFilters === null ? activeCount(this.value) : this.activeFilters;
    },
    segments() {
      return this.segmentDefs || SEGMENTS;
    },
    // Fenster breit genug für die zweite Spalte
    wideWindow() {
      const width = this.$vuetify && this.$vuetify.breakpoint ? this.$vuetify.breakpoint.width : 1280;
      return !this.handy && width >= WIDE_MIN;
    },
    // zweite Spalte: breites Fenster und Platz am Knopf
    wide() {
      return this.wideWindow && this.wideFits;
    },
    columns() {
      return COLUMNS[this.touch ? 'touch' : 'maus'];
    },
    // Einträge der Listen und Zeitpunkte (Schlüssel der Zeitpunkte mit „zeit:“ davor)
    entries() {
      return this.facets
        .map((list) => ({ key: list.key, list }))
        .concat(this.timeDefs.map((time) => ({ key: this.timeKey(time), time })));
    },
    // Eintrag, dessen Auswahl zu sehen ist: der gewählte; breit ohne (gültige) Wahl der erste, damit die zweite Spalte
    // nie leer ist (beim Öffnen setzt der Watcher von facets die Wahl)
    currentEntry() {
      const chosen = this.openList ? this.entries.find((entry) => entry.key === this.openList) || null : null;
      if (chosen || !this.wide) {
        return chosen;
      }
      return this.entries[0] || null;
    },
    currentList() {
      return this.currentEntry && this.currentEntry.list ? this.currentEntry.list : null;
    },
    currentTime() {
      return this.currentEntry && this.currentEntry.time ? this.currentEntry.time : null;
    },
    shownKey() {
      return this.currentEntry ? this.currentEntry.key : null;
    },
    sideOpen() {
      return this.wide && !!this.currentEntry;
    },
    sideId() {
      return 'sale-filter-' + this._uid + '-spalte';
    },
    panelMinHeight() {
      if (this.handy) {
        return MIN_HEIGHT.handy;
      }
      const heights = this.timeDefs.length && this.wide ? MIN_HEIGHT_TIMES : MIN_HEIGHT;
      return this.touch ? heights.touch : heights.maus;
    },
    panelClasses() {
      return {
        'sale-filter__panel--touch': this.touch,
        'sale-filter__panel--handy': this.handy,
        'sale-filter__panel--wide': this.wide
      };
    },
    mainStyle() {
      return this.wide ? { width: this.columns.main + 'px' } : null;
    },
    sideStyle() {
      return { width: this.columns.side + 'px' };
    },
    priceId() {
      return 'sale-filter-' + this._uid + '-preis';
    },
    priceErrorId() {
      return this.priceId + '-fehler';
    },
    minInvalid() {
      return priceInvalid(this.draftMin);
    },
    maxInvalid() {
      return priceInvalid(this.draftMax);
    },
    resultText() {
      const n = this.resultCount;
      if (n === null || n === undefined) {
        return '';
      }
      return formatNumber(n) + ' ' + plural(n, this.resultNouns[0], this.resultNouns[1]);
    }
  },
  watch: {
    // Preisfelder folgen dem Zustand der Seite (z. B. „Filter zurücksetzen“), nicht während der Eingabe
    'value.priceMin': {
      immediate: true,
      handler(v) {
        if (!this.draftTimer) {
          this.draftMin = v || '';
        }
      }
    },
    'value.priceMax': {
      immediate: true,
      handler(v) {
        if (!this.draftTimer) {
          this.draftMax = v || '';
        }
      }
    },
    open(isOpen) {
      if (isOpen) {
        this.place();
        this.measure();
        this.$emit('open');
        this.$nextTick(() => {
          // Lage mit den neuen Listen neu berechnen (VMenu misst sonst den Stand vor dem Öffnen; auf dem Handy ragte
          // das Panel rechts hinaus)
          this.updateMenu();
          this.focusWhenShown(12);
        });
      } else {
        this.flushDrafts();
        // jedes Öffnen beginnt ohne offene Liste
        this.openList = null;
        this.listQuery = '';
        this.$emit('close');
      }
    },
    // Fenster über die Grenze der zweiten Spalte gezogen: Lage neu berechnen
    wide() {
      if (this.open) {
        this.measure();
        this.$nextTick(this.updateMenu);
      }
    },
    // neue Listen (beim Öffnen, neu geladene Zeilen): breit beim Öffnen die erste Liste mit Auswahl, sonst die erste in
    // die zweite Spalte; eine schon gewählte Liste bleibt. Erscheint oder verschwindet die Spalte bei offenem Panel
    // (Zeilen kommen erst jetzt, leerer Zeitraum), Lage neu berechnen.
    facets() {
      if (!this.open || !this.wide) {
        return;
      }
      if (this.openList === null && this.entries.length) {
        const withSelection = this.entries.find((entry) =>
          entry.list ? this.selectedKeys(entry.list.key).length > 0 : !!this.timeSummary(entry.time)
        );
        this.openList = (withSelection || this.entries[0]).key;
      }
      this.$nextTick(this.updateMenu);
    }
  },
  created() {
    // nicht reaktiv: laufende Entprellung der Texteingaben (Betrag, Zeitpunkte)
    this.draftTimer = null;
  },
  mounted() {
    raiseMenu(this.$refs.menu);
  },
  beforeDestroy() {
    clearTimeout(this.draftTimer);
  },
  methods: {
    // Breite und Seite beim Öffnen (und bei Größenänderung, panelMenu.relayout): zweispaltig nur mit Platz am Knopf
    place() {
      const button = this.activator();
      const prefer = !!this.alignRight;
      const double = this.columns.main + this.columns.side + 1 + 2;
      this.wideFits = this.wideWindow && fitsAt(button, double, prefer);
      let width = double;
      if (!this.wideFits) {
        const viewport = document.documentElement.clientWidth || window.innerWidth;
        width = (this.handy ? viewport - 26 : SINGLE[this.touch ? 'touch' : 'maus']) + 2;
      }
      this.rightAligned = alignRightFor(button, width, prefer);
    },
    emitFilters(changes) {
      this.$emit('input', Object.assign({}, this.value, changes));
    },
    patch(key, value) {
      const changes = {};
      changes[key] = value;
      this.emitFilters(changes);
    },
    listId(key) {
      return 'sale-filter-' + this._uid + '-' + key;
    },
    selectedKeys(key) {
      return Array.isArray(this.value[key]) ? this.value[key] : [];
    },
    isChecked(listKey, itemKey) {
      return this.selectedKeys(listKey).indexOf(itemKey) !== -1;
    },
    check(listKey, itemKey, checked) {
      const current = this.selectedKeys(listKey);
      let next = current.filter((key) => key !== itemKey);
      if (checked) {
        next = next.concat([itemKey]);
      }
      this.patch(listKey, next);
    },
    // „Alle“: Auswahl der Liste aufheben; ist sie schon leer, bleibt „Alle“ angehakt
    clearList(listKey) {
      if (this.selectedKeys(listKey).length) {
        this.patch(listKey, []);
      }
    },
    searchable(list) {
      return !!list && list.items.length >= SEARCH_MIN;
    },
    // Statuszeile für Screenreader: Treffer der Listensuche (ohne Suchtext leer)
    matchText(list) {
      if (!this.searchable(list) || !this.listQuery.trim()) {
        return '';
      }
      const n = this.shownItems(list).length;
      return n ? n + ' Treffer' : 'Keine Treffer';
    },
    // gekürzter Wert (mehr als zwei Zeilen): voller Text als Tooltip
    titleIfClipped(e) {
      const row = e.currentTarget;
      const label = row ? row.querySelector('.app-cb__label') : null;
      if (label) {
        const clipped = label.scrollHeight > label.clientHeight + 1 || label.scrollWidth > label.clientWidth + 1;
        row.title = clipped ? label.textContent.replace(/\s+/g, ' ').trim() : '';
      }
    },
    // Werte der Liste, mit Suchtext ohne Groß-/Kleinschreibung und Akzente gefiltert
    shownItems(list) {
      const query = foldText(this.listQuery.trim());
      if (!query || !this.searchable(list)) {
        return list.items;
      }
      return list.items.filter((item) => foldText(item.text).indexOf(query) !== -1);
    },
    // breit: Liste in der zweiten Spalte zeigen (kein Zuklappen, Breite und Höhe des Panels bleiben); mit der Tastatur
    // (Enter, Leertaste: Klick mit detail 0) geht der Fokus in die Spalte. Schmal: Liste darunter auf- und zuklappen.
    toggleList(key, e) {
      if (this.wide) {
        this.showList(key);
        if (e && e.detail === 0) {
          this.$nextTick(this.focusSide);
        }
        return;
      }
      this.openList = this.openList !== key ? key : null;
      this.listQuery = '';
    },
    showList(key) {
      if (this.shownKey !== key) {
        this.listQuery = '';
      }
      this.openList = key;
    },
    // Pfeil rechts am Eintrag: seine Liste zeigen und in die Spalte springen
    onToggleKey(key, e) {
      if (!this.wide || e.key !== 'ArrowRight') {
        return;
      }
      e.preventDefault();
      this.showList(key);
      this.$nextTick(this.focusSide);
    },
    // Pfeil links in der Spalte: zurück zum Eintrag (die Spalte bleibt)
    onSideKey(e) {
      if (e.key === 'ArrowLeft' && !isTextInput(e.target)) {
        e.preventDefault();
        this.focusToggle(this.shownKey);
      }
    },
    // Esc in der zweiten Spalte führt zum Eintrag zurück; sonst schließt VMenu das Panel (also beim zweiten Esc)
    onEsc(e) {
      const side = this.$refs.side;
      if (!this.sideOpen || !side || !side.contains(document.activeElement)) {
        return;
      }
      e.stopPropagation();
      this.focusToggle(this.shownKey);
    },
    focusSide() {
      const side = this.$refs.side;
      const first = side ? side.querySelector('input') : null;
      if (first) {
        first.focus();
      }
    },
    focusToggle(key) {
      const panel = this.$refs.panel;
      const toggle = panel && key ? panel.querySelector('[data-list="' + key + '"]') : null;
      if (toggle) {
        toggle.focus();
      }
    },
    // „alle“, ein Wert mit Namen, sonst „2 ausgewählt“
    summaryText(list) {
      const selected = this.selectedKeys(list.key);
      if (!selected.length) {
        return 'alle';
      }
      if (selected.length === 1) {
        const item = list.items.find((entry) => entry.key === selected[0]);
        return item ? item.text : selected[0];
      }
      return selected.length + ' ausgewählt';
    },
    onPrice(which, text) {
      if (which === 'min') {
        this.draftMin = text;
      } else {
        this.draftMax = text;
      }
      this.scheduleFlush();
    },
    timeKey(time) {
      return 'zeit:' + time.key;
    },
    timeValueOf(time, bound) {
      return this.value[timeFields(time)[bound]] || '';
    },
    timeSummary(time) {
      return shortTimeRange(this.timeValueOf(time, 'from'), this.timeValueOf(time, 'to'));
    },
    onTimeChange(time, texts) {
      const fields = timeFields(time);
      const changes = {};
      changes[fields.from] = texts.from;
      changes[fields.to] = texts.to;
      this.emitFilters(changes);
    },
    // offene Zeitauswahl (zweite Spalte oder unter dem Eintrag; höchstens eine)
    timeEditor() {
      const ref = this.$refs.timeEditor;
      return Array.isArray(ref) ? ref[0] || null : ref || null;
    },
    // Zeitpunkt der offenen Zeitauswahl
    currentTimeForEditor() {
      const entry = this.shownKey ? this.entries.find((item) => item.key === this.shownKey) : null;
      return entry && entry.time ? entry.time : null;
    },
    scheduleFlush() {
      clearTimeout(this.draftTimer);
      this.draftTimer = setTimeout(this.flushDrafts, PRICE_DELAY);
    },
    // eingegebenen Betrag und Zeitpunkte in einem Ereignis übernehmen (zwei Ereignisse nacheinander überschrieben sich:
    // value ist bis zum nächsten Rendern noch der alte Zustand)
    flushDrafts() {
      clearTimeout(this.draftTimer);
      this.draftTimer = null;
      const changes = {};
      if (this.draftMin !== (this.value.priceMin || '') || this.draftMax !== (this.value.priceMax || '')) {
        changes.priceMin = this.draftMin;
        changes.priceMax = this.draftMax;
      }
      const editor = this.timeEditor();
      const pending = editor ? editor.pending() : null;
      if (pending && this.currentTimeForEditor()) {
        const fields = timeFields(this.currentTimeForEditor());
        changes[fields.from] = pending.from;
        changes[fields.to] = pending.to;
      }
      if (Object.keys(changes).length) {
        this.emitFilters(changes);
      }
    },
    // Übernehmen: offenen Betrag und Zeitpunkte übernehmen, Panel schließen, Fokus zurück an den Filter-Knopf
    apply() {
      this.flushDrafts();
      this.open = false;
      this.$nextTick(() => {
        const button = this.activator();
        if (button && button.focus) {
          button.focus();
        }
      });
    },
    reset() {
      clearTimeout(this.draftTimer);
      this.draftTimer = null;
      this.draftMin = '';
      this.draftMax = '';
      this.$emit('input', this.emptyValue ? this.emptyValue() : emptyFilters());
      // „Zurücksetzen“ wird gesperrt und verlöre den Fokus: zurück an den Anfang des Panels
      this.$nextTick(this.focusFirst);
    }
  }
};
</script>

<style scoped>
.sale-filter {
  display: inline-flex;
  flex: none;
  max-width: 100%;
  min-width: 0;
  vertical-align: middle;
}
/* Anzahl aktiver Filter: blaue Pille wie die angehakten Checkboxen, Höhe = Zeilenhöhe des Knopfs (18, Touch 20) */
.sale-filter__badge {
  display: inline-block;
  vertical-align: top;
  box-sizing: border-box;
  min-width: 18px;
  height: 18px;
  margin-left: 6px;
  padding: 0 4px;
  border-radius: 9999px;
  background: var(--link);
  color: var(--on-link, #fff);
  font-size: 11px;
  line-height: 18px;
  font-weight: 600;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.sale-filter__badge--touch {
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  font-size: 12px;
  line-height: 20px;
}
/* ohne aktive Filter unsichtbar, hält aber ihren Platz: Sortierung und Gruppierung rücken nicht, wenn sie erscheint */
.sale-filter__badge--leer {
  visibility: hidden;
}
.sale-filter__panel {
  display: flex;
  flex-direction: column;
  width: 360px;
  /* mit der Kante des Menüs (2 × 1) genau 12 Abstand zu beiden Fensterrändern */
  max-width: calc(100vw - 26px);
  color: var(--ink);
  font-family: var(--font);
}
.sale-filter__panel--touch {
  width: 400px;
}
.sale-filter__panel--handy {
  width: calc(100vw - 26px);
}
/* Fokus am Panel selbst nur nach einem Klick auf eine freie Stelle (Esc und Tab wirken weiter): ohne Rahmen */
.sale-filter__panel:focus {
  outline: none;
}
/* zweite Spalte: Breiten tragen Hauptspalte und Spalte (mainStyle, sideStyle) */
.sale-filter__panel--wide {
  width: auto;
}
.sale-filter__cols {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
}
.sale-filter__body {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 8px 0;
}
/* Scrollschatten (v-scroll-hint): oben bzw. unten verdeckter Inhalt, wie in der Navigationsliste. Klebende Pseudo-
   Elemente ohne Platzbedarf, sichtbar nur mit den Klassen scroll--oben und scroll--unten */
.sale-filter__body::before,
.sale-filter__body::after,
.sale-filter__side-body::before,
.sale-filter__side-body::after {
  content: '';
  position: sticky;
  z-index: 2;
  display: block;
  height: 8px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
}
.sale-filter__body::before,
.sale-filter__side-body::before {
  top: 0;
  margin-bottom: -8px;
  background: radial-gradient(farthest-side at 50% 0, var(--sh-scroll), transparent);
}
.sale-filter__body::after,
.sale-filter__side-body::after {
  bottom: 0;
  margin-top: -8px;
  background: radial-gradient(farthest-side at 50% 100%, var(--sh-scroll), transparent);
}
.sale-filter__body.scroll--oben::before,
.sale-filter__side-body.scroll--oben::before,
.sale-filter__body.scroll--unten::after,
.sale-filter__side-body.scroll--unten::after {
  opacity: 1;
}
.sale-filter__panel--wide .sale-filter__body {
  flex: none;
}
/* seitlich 12 wie die übrigen Menüs der Leiste (app-menu, Zeitraum, Sortierung) */
.sale-filter__block {
  padding: 8px 12px;
}
.sale-filter__label {
  margin-bottom: 6px;
}
/* Segmente über die ganze Breite des Panels */
.sale-filter__block ::v-deep .app-seg {
  display: flex;
}
.sale-filter__block ::v-deep .app-seg__btn {
  flex: 1 1 auto;
}
/* Handy: Segmente schrumpfen und brechen notfalls um (320 px, 400 % Zoom), statt über das Panel hinauszuragen */
.sale-filter__panel--handy .sale-filter__block ::v-deep .app-seg__btn {
  min-width: 0;
  height: auto;
  min-height: var(--h-seg);
  padding: 2px 6px;
  white-space: normal;
  line-height: 16px;
}

/* ---------------------------------------------------------------- Mehrfachlisten */
.sale-filter__lists {
  margin: 8px 0;
  padding: 4px 0;
  border-top: 1px solid var(--line-2);
  border-bottom: 1px solid var(--line-2);
}
/* ohne Umschalter darüber (z. B. Zahlungen): Listen oben ohne Linie */
.sale-filter__lists:first-child {
  margin-top: 0;
  border-top: 0;
}
.sale-filter__toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: var(--h-menu);
  box-sizing: border-box;
  margin: 0;
  padding: 0 12px;
  border: 0;
  background: transparent;
  color: var(--ink);
  font-family: var(--font);
  font-size: var(--fs-menu);
  line-height: var(--lh-menu);
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.sale-filter__toggle:focus:not(:focus-visible) {
  outline: none;
}
:where(html:not(.dichte-touch)) .sale-filter__toggle:hover {
  background: var(--hov);
}
.sale-filter__toggle:active {
  background: var(--press);
}
/* Eintrag, dessen Auswahl in der zweiten Spalte steht: Auswahlton wie gewählte Zeilen */
.sale-filter__toggle.sale-filter__toggle--current {
  background: var(--sel);
}
.sale-filter__toggle--current .sale-filter__chev {
  color: var(--link);
}
/* Fokus: der globale Rahmen (5.5) liegt mit data-flush innen und wird von der scrollenden Spalte nicht abgeschnitten;
   er bleibt auch bei erzwungenen Farben sichtbar */
.sale-filter__name {
  flex: none;
}
.sale-filter__summary {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;
  font-weight: 400;
  color: var(--ink-3);
}
.sale-filter__summary--set {
  font-weight: 500;
  color: var(--ink);
}
.sale-filter__chev {
  flex: none;
  color: var(--ink-3);
}
.sale-filter__options {
  display: flex;
  flex-direction: column;
  padding-bottom: 4px;
}
.sale-filter__option {
  display: flex;
  width: 100%;
  box-sizing: border-box;
  min-height: var(--h-menu);
  padding: 6px 12px;
}
.sale-filter__options .sale-filter__option {
  padding: 6px 12px 6px 24px;
}
/* lange Werte (Ortsnamen) in bis zu zwei Zeilen, darüber hinaus gekürzt mit Tooltip (titleIfClipped) */
.sale-filter__option ::v-deep .app-cb__label {
  display: -webkit-box;
  min-width: 0;
  overflow: hidden;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
.sale-filter__missing {
  color: var(--ink-3);
}
/* Icon vor dem Eintrag (Vertriebskanal der Shop-Tickets, Kundenwunsch 13.10.2026) */
.sale-filter__icon {
  margin-right: 6px;
  vertical-align: -2px;
  color: var(--ink-2);
}
/* Suchfeld einer langen Liste über die Breite der Auswahl */
.sale-filter__find {
  padding: 4px 12px 8px;
}
.sale-filter__options .sale-filter__find {
  padding: 4px 12px 4px 24px;
}
.sale-filter__find .app-search {
  flex: none !important;
  width: auto !important;
}
.sale-filter__none {
  padding: 6px 12px 8px;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink-3);
}
.sale-filter__options .sale-filter__none {
  padding-left: 24px;
}

/* ---------------------------------------------------------------- zweite Spalte */
.sale-filter__side {
  position: relative;
  flex: none;
  border-left: 1px solid var(--line-2);
}
/* Inhalt außerhalb des Flusses: Die linke Spalte bestimmt die Höhe, diese Spalte streckt sich darauf und scrollt */
.sale-filter__side-inner {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
}
/* Titel auf der Höhe der ersten Segment-Beschriftung links (8 + 8 Polster) */
.sale-filter__side-head {
  flex: none;
  margin: 0;
  padding: 16px 12px 6px;
}
.sale-filter__side .sale-filter__find {
  flex: none;
}
.sale-filter__side-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-bottom: 8px;
}

/* ---------------------------------------------------------------- Preis */
.sale-filter__price {
  display: flex;
  align-items: center;
  gap: 8px;
}
.sale-filter__price .app-field {
  flex: 1 1 0;
}
.sale-filter__dash,
.sale-filter__unit {
  flex: none;
  color: var(--ink-3);
  font-size: var(--fs-ctl);
}
.sale-filter__error {
  margin-top: 6px;
}

/* ---------------------------------------------------------------- Zeitpunkte unter dem Eintrag (schmal) */
.sale-filter__options--time {
  padding: 4px 0 4px 12px;
}

/* ---------------------------------------------------------------- Fuß */
.sale-filter__foot {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border-top: 1px solid var(--line-2);
}
.sale-filter__foot-actions {
  display: flex;
  flex: none;
  gap: 8px;
}
.sale-filter__result {
  min-width: 0;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink-2);
  font-variant-numeric: tabular-nums;
}
</style>
